/**
 * Logo framing editor — the step between "chose a file" and "uploaded bytes".
 *
 * A tenant logo is rendered downstream in a 48px CIRCLE (kiosk masthead) and a
 * 40px rounded square (checkout hero + QR-credit hero). Left to CSS, a wide or
 * padded source either shrinks to a sliver or gets center-cropped with no way
 * to influence it. This dialog hands that decision to the uploader — zoom, pan,
 * and matte — then bakes the result into the square PNG we actually store, so
 * every surface shows what they framed here.
 *
 * The two chips under the viewport are live renders at the real downstream
 * sizes, from the same draw call: the uploader sees the circle crop BEFORE
 * saving rather than discovering it on the tablet.
 *
 * Shared by the ops console and the tenant portal (`@parkify/ui/logo-adjuster`).
 * Until 2026-10-22 each app had its own copy, 19 lines apart. Reconciled as:
 *   - caption and helper text use --color-gray-600 (tenant-portal's fix: at
 *     10-12px, gray-400/500 read at under 2:1 and the two preview captions are
 *     the only thing telling them apart);
 *   - the chip radius is the --radius-chip token (ops), not a literal 8;
 *   - the active chip is `var(--color-gray-800, #231F20)` (ops): ops' own
 *     neutral ramp defines gray-800, and in the tenant portal it is undefined
 *     and falls back to the same #231F20 its old --color-black gave.
 * Renders exactly as before in the tenant portal; in ops only the three
 * caption colours darken.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'
import {
  DEFAULT_ADJUST, MIN_ZOOM, clampAdjust, composeLogo, drawLogo, fillZoom,
  layoutLogo, maxZoomFor, type LogoAdjust, type LogoSource,
} from './brand/logoBrand'

const VIEWPORT = 260   // editor square, CSS px
const KIOSK = 48       // staff masthead circle
const CHECKOUT = 40    // consumer hero rounded square
const NUDGE = 0.06     // arrow-key pan, in units of available slack

export interface LogoAdjusterResult {
  blob: Blob
  dataUrl: string
  adjust: LogoAdjust
}

interface Props {
  source: LogoSource
  /** Framing to open with — defaults to "contain, slightly inset". */
  initial?: LogoAdjust
  /** Brand color behind the checkout chip, so the preview matches the real hero. */
  accent?: string
  onCancel: () => void
  onApply: (result: LogoAdjusterResult) => void
}

export default function LogoAdjuster({ source, initial, accent, onCancel, onApply }: Props) {
  // Zoom ceiling is per-image so "Fill frame" is always reachable on the slider
  // (a wide logo needs more than the baseline 3x to cover a square).
  const maxZoom = useMemo(() => maxZoomFor(source.width, source.height), [source])
  const [adjust, setAdjust] = useState<LogoAdjust>(() => clampAdjust(initial ?? DEFAULT_ADJUST, maxZoom))
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mainRef = useRef<HTMLCanvasElement | null>(null)
  const kioskRef = useRef<HTMLCanvasElement | null>(null)
  const checkoutRef = useRef<HTMLCanvasElement | null>(null)
  const dragRef = useRef<{ x: number; y: number } | null>(null)

  const patch = useCallback((p: Partial<LogoAdjust>) => {
    setAdjust(a => clampAdjust({ ...a, ...p }, maxZoom))
  }, [maxZoom])

  // Paint the viewport and both preview chips from one adjust. Canvases are
  // sized in device pixels so the 40/48px chips stay sharp on a retina panel;
  // the layout is normalized, so the same adjust maps to every size for free.
  useEffect(() => {
    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1))
    for (const [ref, css] of [[mainRef, VIEWPORT], [kioskRef, KIOSK], [checkoutRef, CHECKOUT]] as const) {
      const canvas = ref.current
      if (!canvas) continue
      const px = Math.round(css * dpr)
      if (canvas.width !== px) { canvas.width = px; canvas.height = px }
      const ctx = canvas.getContext('2d')
      if (ctx) drawLogo(ctx, source, adjust, px)
    }
  }, [source, adjust])

  // Wheel-to-zoom needs a non-passive listener to suppress the page scroll
  // underneath; React's onWheel is registered passive and can't preventDefault.
  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setAdjust(a => clampAdjust({ ...a, zoom: a.zoom * (1 - e.deltaY * 0.0015) }, maxZoom))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [maxZoom])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  // Pan. Slack is measured at the CSS size so a pointer delta in CSS px maps
  // 1:1 to the crop — drag feels like moving the image, not a scaled proxy.
  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    dragRef.current = { x: e.clientX, y: e.clientY }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const start = dragRef.current
    if (!start) return
    const { slackX, slackY } = layoutLogo(source.width, source.height, VIEWPORT, adjust)
    const dx = e.clientX - start.x
    const dy = e.clientY - start.y
    dragRef.current = { x: e.clientX, y: e.clientY }
    patch({
      offsetX: adjust.offsetX + (slackX > 0 ? dx / slackX : 0),
      offsetY: adjust.offsetY + (slackY > 0 ? dy / slackY : 0),
    })
  }
  const endDrag = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    dragRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }
  const onKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    const map: Record<string, [number, number]> = {
      ArrowLeft: [-NUDGE, 0], ArrowRight: [NUDGE, 0], ArrowUp: [0, -NUDGE], ArrowDown: [0, NUDGE],
    }
    const d = map[e.key]
    if (!d) return
    e.preventDefault()
    patch({ offsetX: adjust.offsetX + d[0], offsetY: adjust.offsetY + d[1] })
  }

  const apply = async () => {
    if (applying) return
    setApplying(true)
    setError(null)
    try {
      const { blob, dataUrl } = await composeLogo(source, adjust)
      onApply({ blob, dataUrl, adjust })
    } catch (err) {
      console.error('[LogoAdjuster] compose:', err)
      setError('Could not render that logo. Try a different image.')
      setApplying(false)
    }
  }

  const fill = fillZoom(source.width, source.height)
  const heroBg = accent && /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : '#7F3F98'

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Adjust logo">
      <div style={modal}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>Adjust logo</div>
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', lineHeight: 1.45, marginBottom: 14 }}>
          Drag to reposition, zoom to fill the frame. What you frame here is exactly what parkers and staff see.
        </div>

        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div>
            <div style={checkerFrame}>
              <canvas
                ref={mainRef}
                tabIndex={0}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={onKeyDown}
                aria-label="Logo framing — drag to reposition, arrow keys to nudge"
                style={{ width: VIEWPORT, height: VIEWPORT, display: 'block', cursor: 'grab', touchAction: 'none', outline: 'none' }}
              />
            </div>
            <input
              type="range" min={MIN_ZOOM} max={maxZoom} step={0.01} value={adjust.zoom}
              onChange={e => patch({ zoom: Number(e.target.value) })}
              aria-label="Zoom"
              style={{ width: '100%', marginTop: 12, accentColor: heroBg, cursor: 'pointer' }}
            />
          </div>

          <div style={{ flex: 1, minWidth: 190, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={label}>Framing</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" style={chip(false)} onClick={() => setAdjust(a => ({ ...a, ...DEFAULT_ADJUST, background: a.background }))}>
                  Fit whole logo
                </button>
                <button type="button" style={chip(false)} onClick={() => patch({ zoom: fill, offsetX: 0, offsetY: 0 })}>
                  Fill frame
                </button>
              </div>
            </div>

            <div>
              <div style={label}>Background</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" style={chip(adjust.background === 'transparent')} onClick={() => patch({ background: 'transparent' })}>
                  Transparent
                </button>
                <button type="button" style={chip(adjust.background === 'white')} onClick={() => patch({ background: 'white' })}>
                  White
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-gray-600)', marginTop: 6, lineHeight: 1.4 }}>
                Pick White if your logo is light-colored — it keeps it readable on a colored header.
              </div>
            </div>

            <div>
              <div style={label}>How it will look</div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...chipFrame, background: '#fff', borderRadius: '50%', width: KIOSK, height: KIOSK }}>
                    <canvas ref={kioskRef} style={{ width: KIOSK, height: KIOSK, display: 'block', borderRadius: '50%' }} />
                  </div>
                  <div style={chipCaption}>Staff tablet</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...chipFrame, background: heroBg, borderRadius: 8, width: CHECKOUT, height: CHECKOUT }}>
                    <canvas ref={checkoutRef} style={{ width: CHECKOUT, height: CHECKOUT, display: 'block', borderRadius: 8 }} />
                  </div>
                  <div style={chipCaption}>Checkout</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600, marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onCancel} disabled={applying} style={chip(false)}>Cancel</button>
          <button
            type="button" onClick={apply} disabled={applying}
            style={{ ...chip(false), background: heroBg, color: '#fff', borderColor: heroBg, opacity: applying ? 0.6 : 1 }}
          >
            {applying ? 'Applying…' : 'Use this logo'}
          </button>
        </div>
      </div>
    </div>
  )
}

const overlay: CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(35,31,32,0.6)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16,
}
const modal: CSSProperties = {
  width: 'min(560px, 96vw)', maxHeight: '94vh', overflowY: 'auto', background: '#fff',
  borderRadius: 16, padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
}
// Alpha checkerboard, so a transparent matte reads as transparent (not white).
const checkerFrame: CSSProperties = {
  width: VIEWPORT, height: VIEWPORT, borderRadius: 12, overflow: 'hidden',
  border: '1.5px solid var(--color-gray-300)',
  backgroundColor: '#fff',
  backgroundImage:
    'linear-gradient(45deg, #eceaee 25%, transparent 25%), linear-gradient(-45deg, #eceaee 25%, transparent 25%),' +
    'linear-gradient(45deg, transparent 75%, #eceaee 75%), linear-gradient(-45deg, transparent 75%, #eceaee 75%)',
  backgroundSize: '14px 14px',
  backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0',
}
const chipFrame: CSSProperties = { display: 'inline-flex', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }
// gray-600, not gray-400: these caption the two logo previews ("Staff tablet"
// / "Checkout") and are the only thing telling them apart. 10px at 1.69:1 is
// not a de-emphasised label, it is an invisible one.
const chipCaption: CSSProperties = { fontSize: 10, color: 'var(--color-gray-600)', marginTop: 5, fontWeight: 600 }
const label: CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
  color: 'var(--color-gray-600)', marginBottom: 6,
}
function chip(active: boolean): CSSProperties {
  return {
    padding: '7px 12px', borderRadius: 'var(--radius-chip)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit', lineHeight: 1,
    border: `1.5px solid ${active ? 'var(--color-gray-800, #231F20)' : 'var(--color-gray-300)'}`,
    background: active ? 'var(--color-gray-800, #231F20)' : 'transparent',
    color: active ? '#fff' : 'var(--color-gray-700)',
  }
}
